import type { OperationContext } from './operation';

export type OperationRun = Readonly<{
  operation: OperationContext;
  isPublishable(): boolean;
  publish(action: () => void): boolean;
  settle(action: () => void): boolean;
  invalidate(): void;
}>;

export type OperationRuns = Readonly<{
  begin(channel: string, operation: OperationContext): OperationRun;
  invalidate(channel: string): void;
  invalidateAll(): void;
}>;

type ChannelState = {
  token: number;
};

export const createOperationRuns = (isMounted: () => boolean): OperationRuns => {
  const channels = new Map<string, ChannelState>();

  const nextToken = (channel: string): number => {
    const token = (channels.get(channel)?.token ?? 0) + 1;
    channels.set(channel, { token });
    return token;
  };

  const invalidate = (channel: string): void => {
    nextToken(channel);
  };

  return {
    begin(channel, operation) {
      const token = nextToken(channel);
      let invalidated = false;
      const ownsChannel = (): boolean => !invalidated && isMounted() && channels.get(channel)?.token === token;

      return Object.freeze({
        operation,
        isPublishable: () => ownsChannel() && operation.isScopeCurrent(),
        publish(action) {
          if (!ownsChannel() || !operation.isScopeCurrent()) return false;
          action();
          return true;
        },
        settle(action) {
          if (!ownsChannel()) return false;
          action();
          return true;
        },
        invalidate() {
          if (invalidated) return;
          invalidated = true;
          if (channels.get(channel)?.token === token) invalidate(channel);
        },
      });
    },
    invalidate,
    invalidateAll() {
      for (const channel of channels.keys()) invalidate(channel);
    },
  };
};
